import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FormattedMessage } from "react-intl";
import { Heading, Tabs, TabsContent, TabsList, TabsTrigger, Text } from "@lendcord/concord";
import { useSession } from "@/app/session";
import { AuthHeader } from "./components/AuthHeader";
import { BrandPanel } from "./components/BrandPanel";
import { CredentialsForm } from "./components/CredentialsForm";
import { ForgotPasswordForm } from "./components/ForgotPasswordForm";
import { LoginForm } from "./components/LoginForm";
import { OtpForm } from "./components/OtpForm";
import { useSignupMutation, type AuthTokenResponse } from "./api/auth.api";
import { toIndianE164 } from "./validators";
import type { AuthMode, AuthStep, CredentialsFormValues } from "./auth.types";

export function AuthPage() {
  const { login } = useSession();
  const [signup] = useSignupMutation();
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState<AuthMode>(searchParams.get("mode") === "signup" ? "signup" : "login");
  const [step, setStep] = useState<AuthStep>("credentials");
  const [sessionToken, setSessionToken] = useState("");
  // Cached so "Resend" can replay the same signup call — there's no separate
  // resend endpoint, only /auth/signup itself (see OtpForm's onResend).
  const [signupValues, setSignupValues] = useState<CredentialsFormValues | null>(null);

  async function requestSignup(values: CredentialsFormValues) {
    const { sessionToken: token } = await signup({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      phone: toIndianE164(values.phone),
      password: values.password,
      role: values.role,
      consentGiven: values.consentGiven,
    }).unwrap();
    setSessionToken(token);
    setSignupValues(values);
    setStep("otp");
  }

  async function handleResend() {
    if (!signupValues) return;
    await requestSignup(signupValues);
  }

  function handleModeChange(nextMode: string) {
    setMode(nextMode as AuthMode);
  }

  function handleAuthenticated(result: AuthTokenResponse) {
    // No navigate() call here on purpose — flipping isAuthenticated is
    // enough. RedirectIfAuthenticated (wrapping /auth, see App.tsx) reacts
    // to that and sends the user to wherever RequireAuth originally
    // redirected them from, or the dashboard by default. AuthPage used to
    // also navigate() here, which raced RedirectIfAuthenticated's own
    // effect-driven redirect and always lost, ignoring the "come back here
    // after login" destination.
    login(result.accessToken, result.mustChangePassword);
  }

  function handleEditPhone() {
    setStep("credentials");
  }

  function handleForgotPassword() {
    setMode("login");
    setStep("forgot-password");
  }

  function handleBackToLogin() {
    setStep("credentials");
  }

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <AuthHeader />

      <div className="flex flex-1 flex-col md:flex-row">
        <BrandPanel />

        <div className="flex flex-1 justify-center px-6 py-10 md:px-12">
          <div className="w-full max-w-sm">
            {step === "credentials" && (
              <Tabs value={mode} onValueChange={handleModeChange}>
                <TabsList className="mb-6 grid grid-cols-2">
                  <TabsTrigger value="login">
                    <FormattedMessage id="auth.authPage.loginTab" />
                  </TabsTrigger>
                  <TabsTrigger value="signup">
                    <FormattedMessage id="auth.authPage.signupTab" />
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login" className="mt-0">
                  <Heading level={2} className="mb-1">
                    <FormattedMessage id="auth.authPage.welcomeBackHeading" />
                  </Heading>
                  <Text size="sm" variant="muted" className="mb-6">
                    <FormattedMessage id="auth.authPage.welcomeBackSubtitle" />
                  </Text>
                  <LoginForm onSuccess={handleAuthenticated} onForgotPassword={handleForgotPassword} />
                </TabsContent>

                <TabsContent value="signup" className="mt-0">
                  <Heading level={2} className="mb-1">
                    <FormattedMessage id="auth.authPage.createAccountHeading" />
                  </Heading>
                  <Text size="sm" variant="muted" className="mb-6">
                    <FormattedMessage id="auth.authPage.createAccountSubtitle" />
                  </Text>
                  <CredentialsForm onSubmit={requestSignup} />
                </TabsContent>
              </Tabs>
            )}

            {step === "otp" && signupValues && (
              <div>
                <Heading level={2} className="mb-1">
                  <FormattedMessage id="auth.authPage.enterCodeHeading" />
                </Heading>
                <Text size="sm" variant="muted" className="mb-6">
                  <FormattedMessage id="auth.authPage.enterCodeSubtitleSignup" />
                </Text>
                <OtpForm
                  phone={signupValues.phone}
                  email={signupValues.email}
                  sessionToken={sessionToken}
                  onResend={handleResend}
                  onVerified={handleAuthenticated}
                  onEditPhone={handleEditPhone}
                />
              </div>
            )}

            {step === "forgot-password" && (
              <ForgotPasswordForm onSuccess={handleAuthenticated} onBack={handleBackToLogin} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
